import type { ReactNode } from 'react'
import { format } from 'date-fns'
import * as Duration from 'dration'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import {
  DataTable,
  type DataTableColumn,
  type DataTableSelection,
} from '@/components/DataTable'
import { CellText } from '@/components/DataTable/CellText'
import {
  TableActions,
  TableEmptyView,
  TableScroll,
  TableSearch,
  TableTab,
  TableTabs,
  TableToolbar,
  TableView,
} from '@/components/DataTable/TableView'

import { IconButton } from '@/components/IconButton'
import type { Id } from '@/infra/id'
import { timeago } from '@/utils/date-util'
import { match } from '@/utils/pattern-match-util'
import styles from './UserList.module.css'

/**
 * Clients have no user type and can't be selected.
 */
export type UserListVariant = 'users' | 'clients'

export type UserListColumnId =
  'name' | 'email' | 'userType' | 'lastLogin' | 'dateCreated'

export const userListColumns: {
  id: UserListColumnId
  header: string
  defaultWidth: number
}[] = [
  { id: 'name', header: 'Name', defaultWidth: 260 },
  { id: 'email', header: 'Email', defaultWidth: 280 },
  { id: 'userType', header: 'User type', defaultWidth: 140 },
  { id: 'lastLogin', header: 'Last login', defaultWidth: 140 },
  { id: 'dateCreated', header: 'Created', defaultWidth: 140 },
]

export interface UserListRow {
  publicId: Id
  displayName: string
  avatar: string | null
  online: boolean
  email: string
  userType?: string | null
  lastSeen: Date | null
  dateCreated: Date
  href: string
  isSelected?: boolean
  actions?: ReactNode
}

export interface UserListSelection {
  isShowing: boolean
  onToggleShowing: () => void
  all: DataTableSelection<UserListRow>['all']
  onToggleAll: DataTableSelection<UserListRow>['onToggleAll']
  onToggleRow: (row: UserListRow, checked: boolean) => void
}

export interface UserListProps {
  variant: UserListVariant
  isShowingInactiveUsers: boolean
  onInactiveChange: (value: boolean) => void
  totalUsers: number
  showStatusTabs: boolean
  searchText: string
  onSearchTextChange: (value: string) => void
  filterButton?: ReactNode
  appliedFilters?: ReactNode
  actionsMenu?: ReactNode
  selection?: UserListSelection
  columnWidths: Record<UserListColumnId, number>
  onResize: (columnId: string, width: number) => void
  onResizeEnd: () => void
  showEmptyState: boolean
  emptyState: ReactNode
  rows: UserListRow[]
  /**
   * Renders the column headers as skeletons, while the first users load.
   */
  isSkeleton?: boolean
  scrollLoader: ReactNode
}

/**
 * The user or client table in the refresh design, laid out like the job list.
 */
export function UserList({
  variant,
  isShowingInactiveUsers,
  onInactiveChange,
  totalUsers,
  showStatusTabs,
  searchText,
  onSearchTextChange,
  filterButton,
  appliedFilters,
  actionsMenu,
  selection,
  columnWidths,
  onResize,
  onResizeEnd,
  showEmptyState,
  isSkeleton,
  emptyState,
  rows,
  scrollLoader,
}: UserListProps) {
  const columns = userListColumns
    .filter((column) => variant === 'users' || column.id !== 'userType')
    .map((column): DataTableColumn<UserListRow> => ({
      id: column.id,
      header: column.header,
      width: columnWidths[column.id],
      linked: column.id === 'name',
      render: (user) => renderCell(column.id, user),
    }))

  return (
    <TableView>
      <TableToolbar>
        <TableTabs>
          {showStatusTabs &&
            [false, true].map((value) => (
              <TableTab
                key={String(value)}
                isSelected={isShowingInactiveUsers === value}
                onPress={() => onInactiveChange(value)}
                testId={`user-list-status-${value ? 'inactive' : 'active'}`}
              >
                {value ? 'Inactive' : 'Active'}
              </TableTab>
            ))}
          <span className={styles.count} data-test="users-table-count">
            {formatUserCount(totalUsers)}
          </span>
        </TableTabs>
        <TableActions>
          <TableSearch
            value={searchText}
            onChange={onSearchTextChange}
            placeholder={match(variant, {
              users: () => 'Search users',
              clients: () => 'Search clients',
            })}
            testId={match(variant, {
              users: () => 'user-search-field',
              clients: () => 'client-search-field',
            })}
          />
          {filterButton}
          {selection && (
            <Button
              label="Select"
              size="small"
              iconLeft="select"
              aria-pressed={selection.isShowing}
              onPress={selection.onToggleShowing}
              testId="user-list-toggle-selection"
            />
          )}
          {actionsMenu}
        </TableActions>
      </TableToolbar>
      {appliedFilters}
      <TableScroll>
        {showEmptyState ? (
          <TableEmptyView>{emptyState}</TableEmptyView>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(user) => String(user.publicId)}
            rowHref={(user) => user.href}
            rowTestId={(user) =>
              match(variant, {
                users: () => `user-list-item-${user.publicId}`,
                clients: () => `client-list-item-${user.publicId}`,
              })
            }
            selection={
              selection?.isShowing
                ? {
                    all: selection.all,
                    onToggleAll: selection.onToggleAll,
                    isSelected: (user) => !!user.isSelected,
                    onToggle: selection.onToggleRow,
                  }
                : undefined
            }
            rowActions={
              variant === 'users' ? (user) => user.actions : undefined
            }
            onResize={onResize}
            onResizeEnd={onResizeEnd}
            isSkeleton={isSkeleton}
            footer={scrollLoader}
          />
        )}
      </TableScroll>
    </TableView>
  )
}

/**
 * The toolbar button that opens the bulk actions menu.
 */
export function UserListActionsButton() {
  return (
    <IconButton
      name="more"
      label="Actions"
      testId="user-management-table-menu"
      rotate={90}
    />
  )
}

function renderCell(id: UserListColumnId, user: UserListRow) {
  switch (id) {
    case 'name':
      return (
        <span className={styles.name}>
          <Avatar size={20} subject={{ avatar: user.avatar }} />
          <span className={styles.nameText}>{user.displayName}</span>
          {user.online && <span className={styles.online} title="Online" />}
        </span>
      )
    case 'email':
      return <CellText variant="truncate">{user.email}</CellText>
    case 'userType':
      return user.userType ?? <CellText variant="empty">—</CellText>
    case 'lastLogin':
      return user.lastSeen ? (
        <UserDateText date={user.lastSeen} />
      ) : (
        <CellText variant="empty">—</CellText>
      )
    case 'dateCreated':
      return <UserDateText date={user.dateCreated} />
  }
}

function UserDateText({ date }: { date: Date }) {
  return (
    <CellText variant="tabular" title={format(date, 'PPpp')}>
      {timeago(date, { cutoff: Duration.fromDays(7) })}
    </CellText>
  )
}

function formatUserCount(count: number) {
  const n = Intl.NumberFormat('en-US').format(count)
  return count === 1 ? `${n} user` : `${n} users`
}
